import React, { useState, useEffect } from 'react';
import { collection, query, onSnapshot, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useUserRole } from '../../hooks/useUserRole';
import { CheckCircle, XCircle, AlertTriangle, UserCog, Trash2, Edit } from 'lucide-react';
import { UserModal } from './UserModal';
import { ConfirmationModal } from '../common/ConfirmationModal';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'rider' | 'co-rider' | 'manager';
  approved: boolean;
  createdAt: string;
}

export const MembersList = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [error, setError] = useState<string | null>(null);
  const { isManager } = useUserRole();
  const [selectedRole, setSelectedRole] = useState<'all' | 'rider' | 'co-rider' | 'manager'>('all');
  const [approvalStatus, setApprovalStatus] = useState<'all' | 'approved' | 'pending'>('all');
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'users'));
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const usersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as User[];
        setUsers(usersData);
      },
      (err) => {
        console.error('Error loading users:', err);
        setError('Benutzer konnten nicht geladen werden');
      }
    );

    return () => unsubscribe();
  }, []);

const handleApprovalChange = async (userId: string, approved: boolean) => {
  try {
    const updates: Partial<User> = { approved };
    
    // If the user is being approved, set emailVerified to true
    if (approved) {
      updates.emailVerified = true;
    }

    await updateDoc(doc(db, 'users', userId), updates);
  } catch (error) {
    console.error('Error updating user approval:', error);
    setError('Benutzerstatus konnte nicht aktualisiert werden');
  }
};


  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      await deleteDoc(doc(db, 'users', userToDelete.id));
      setUserToDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Benutzer konnte nicht gelöscht werden');
    }
  };

  const handleUpdateUser = async (userData: Partial<User>) => {
    if (!editingUser) return;

    try {
      await updateDoc(doc(db, 'users', editingUser.id), userData);
      setEditingUser(null);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Benutzerdaten konnten nicht aktualisiert werden');
    }
  };

  const getRoleBadgeStyle = (role: string) => {
    switch (role) {
      case 'manager':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200';
      case 'rider':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'co-rider':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'manager':
        return 'Verwalter';
      case 'rider':
        return 'Reiter';
      case 'co-rider':
        return 'Mitreiter';
      default:
        return role;
    }
  };

  const filteredUsers = users.filter(user => {
    const roleMatch = selectedRole === 'all' || user.role === selectedRole;
    const approvalMatch = approvalStatus === 'all' || 
      (approvalStatus === 'approved' && user.approved) ||
      (approvalStatus === 'pending' && !user.approved);
    return roleMatch && approvalMatch;
  });

  if (!isManager) {
    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/50 border-l-4 border-yellow-400 dark:border-yellow-500 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <AlertTriangle className="h-5 w-5 text-yellow-400 dark:text-yellow-300" />
          </div>
          <div className="ml-3">
            <p className="text-sm text-yellow-700 dark:text-yellow-200">
              Sie haben keine Berechtigung, diese Seite zu sehen.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Benutzerverwaltung</h2>
        <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-4">
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value as any)}
            className="rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="all">Alle Rollen</option>
            <option value="rider">Reiter</option>
            <option value="co-rider">Mitreiter</option>
            <option value="manager">Verwalter</option>
          </select>
          <select
            value={approvalStatus}
            onChange={(e) => setApprovalStatus(e.target.value as any)}
            className="rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
          >
            <option value="all">Alle Status</option>
            <option value="approved">Bestätigt</option>
            <option value="pending">Ausstehend</option>
          </select>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-red-400 dark:text-red-300" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {filteredUsers.map((user) => (
            <li key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <div className="px-4 py-4 sm:px-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex items-center min-w-0">
                    <div className="flex-shrink-0">
                      <UserCog className={`h-6 w-6 ${
                        user.role === 'manager' ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-400 dark:text-gray-500'
                      }`} />
                    </div>
                    <div className="ml-4 truncate">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white truncate">{user.name}</h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeStyle(user.role)}`}>
                      {getRoleLabel(user.role)}
                    </span>
                    
                    <div className="flex items-center space-x-2">
                      {user.role !== 'manager' && (
                        <>
                          <button
                            onClick={() => handleApprovalChange(user.id, true)}
                            className={`p-1 rounded-full ${
                              user.approved
                                ? 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900'
                                : 'text-gray-400 hover:text-green-600 hover:bg-green-100 dark:hover:text-green-400 dark:hover:bg-green-900'
                            }`}
                            title="Benutzer bestätigen"
                          >
                            <CheckCircle className="h-6 w-6" />
                          </button>
                          <button
                            onClick={() => handleApprovalChange(user.id, false)}
                            className={`p-1 rounded-full ${
                              !user.approved
                                ? 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900'
                                : 'text-gray-400 hover:text-red-600 hover:bg-red-100 dark:hover:text-red-400 dark:hover:bg-red-900'
                            }`}
                            title="Bestätigung zurückziehen"
                          >
                            <XCircle className="h-6 w-6" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => {
                          setEditingUser(user);
                          setIsModalOpen(true);
                        }}
                        className="p-1 rounded-full text-gray-400 hover:text-indigo-600 hover:bg-indigo-100 dark:hover:text-indigo-400 dark:hover:bg-indigo-900"
                        title="Benutzer bearbeiten"
                      >
                        <Edit className="h-6 w-6" />
                      </button>
                      <button
                        onClick={() => setUserToDelete(user)}
                        className="p-1 rounded-full text-gray-400 hover:text-red-600 hover:bg-red-100 dark:hover:text-red-400 dark:hover:bg-red-900"
                        title="Benutzer löschen"
                      >
                        <Trash2 className="h-6 w-6" />
                      </button>
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Registriert am {new Date(user.createdAt).toLocaleDateString('de-DE', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <UserModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingUser(null);
        }}
        onSave={handleUpdateUser}
        user={editingUser}
      />

      <ConfirmationModal
        isOpen={!!userToDelete}
        onClose={() => setUserToDelete(null)}
        onConfirm={handleDeleteUser}
        title="Benutzer löschen"
        message={`Möchten Sie den Benutzer "${userToDelete?.name}" wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.`}
        confirmLabel="Löschen"
        confirmStyle="danger"
      />
    </div>
  );
};
